import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ComponentStoreService } from '@core/services/component-store.service';
import { LayoutService } from '@core/services/layout.service';
import { SinkholeDetailsModel } from '@entities/covered-location/covered-location.model';
import { ViewportType } from '@entities/layout/layout.entity';
import { ComponentStore } from '@ngrx/component-store';
import { GeneralUtils } from '@shared/utils/general.utils';
import { Nullable } from '@shared/utils/type.utils';
import { Observable, Subject, distinctUntilChanged, takeUntil } from 'rxjs';

export interface SinkholeFormState {
  insideModal?: boolean;
}

@Component({
    selector: 'nwx-sinkhole-details',
    templateUrl: './sinkhole-details.component.html',
    styleUrl: './sinkhole-details.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ComponentStoreService, ComponentStore],
    standalone: false
})

export class SinkholeDetailsComponent implements OnInit, OnDestroy {
  @Input() sinkholeDetails: Nullable<SinkholeDetailsModel> = {} as SinkholeDetailsModel;
  @Input() insideModal = false;

  @Output() valueChange = new EventEmitter<Partial<SinkholeDetailsModel>>();
  @Output() formReady = new EventEmitter<FormGroup>();

  horizontalRadio = false;
  vm$!: Observable<SinkholeFormState>;
  form!: FormGroup;
  errorMessage: { [key: string]: string } = {};

  private unsubscribe$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private readonly componentStore: ComponentStoreService<SinkholeFormState>,
    private layoutService: LayoutService,
    private cd: ChangeDetectorRef
  ) {
    this.layoutService
      .getViewport()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((viewport) => {
        const displayHorizontal: ViewportType[] = [
          'mini',
          'medium',
          'large',
          'xlarge',
          'xxlarge',
        ];
        this.horizontalRadio = displayHorizontal.includes(viewport);
      });
  }

  ngOnInit(): void {
    this.form = this.buildForm(this.sinkholeDetails);

    this.form.valueChanges
      .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged(this.hasChanges))
      .subscribe((changes: SinkholeDetailsModel) => {
        this.emitChanges(changes);
    });

    this.formReady.emit(this.form);

    this.componentStore.initialize({
      insideModal: this.insideModal,
    });
  
    this.vm$ = this.componentStore.get();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  emitChanges(changes: Partial<SinkholeDetailsModel>): void {
    this.valueChange.emit({
      hasPropertyExperiencedSinkholeDamage: GeneralUtils.parseBooleanFromString(
        changes.hasPropertyExperiencedSinkholeDamage
      ),
      hasPropertyHadSinkholeActivity: GeneralUtils.parseBooleanFromString(
        changes.hasPropertyHadSinkholeActivity
      ),
      hasApplicantReportedSinkholeDamage: GeneralUtils.parseBooleanFromString(
        changes.hasApplicantReportedSinkholeDamage
      ),
      doesApplicantKnowOfSinkholeDamage: GeneralUtils.parseBooleanFromString(
        changes.doesApplicantKnowOfSinkholeDamage
      ),
      doesApplicantKnowPriorOwnerReportedSinkholeDamage: GeneralUtils.parseBooleanFromString(
        changes.doesApplicantKnowPriorOwnerReportedSinkholeDamage
      ),
    })
  }

  hasChanges(a: SinkholeDetailsModel, b: SinkholeDetailsModel): boolean {
    return (
      a?.hasPropertyExperiencedSinkholeDamage === b?.hasPropertyExperiencedSinkholeDamage &&
      a?.hasPropertyHadSinkholeActivity === b?.hasPropertyHadSinkholeActivity &&
      a?.hasApplicantReportedSinkholeDamage === b?.hasApplicantReportedSinkholeDamage &&
      a?.doesApplicantKnowOfSinkholeDamage === b?.doesApplicantKnowOfSinkholeDamage &&
      a?.doesApplicantKnowPriorOwnerReportedSinkholeDamage === b?.doesApplicantKnowPriorOwnerReportedSinkholeDamage
    );
  }

  buildForm(sinkholeDetails: Nullable<SinkholeDetailsModel>): FormGroup {
    return this.fb.group({
      hasPropertyExperiencedSinkholeDamage: this.fb.control(
        GeneralUtils.parseStringFromBoolean(sinkholeDetails?.hasPropertyExperiencedSinkholeDamage)
        ?? 'false',
        [Validators.required]
      ),
      hasPropertyHadSinkholeActivity: this.fb.control(
        GeneralUtils.parseStringFromBoolean(sinkholeDetails?.hasPropertyHadSinkholeActivity)
        ?? 'false',
        [Validators.required]
      ),
      hasApplicantReportedSinkholeDamage: this.fb.control(
        GeneralUtils.parseStringFromBoolean(sinkholeDetails?.hasApplicantReportedSinkholeDamage)
        ?? 'false',
        [Validators.required]
      ),
      doesApplicantKnowOfSinkholeDamage: this.fb.control(
        GeneralUtils.parseStringFromBoolean(sinkholeDetails?.doesApplicantKnowOfSinkholeDamage)
        ?? 'false',
        [Validators.required]
      ),
      doesApplicantKnowPriorOwnerReportedSinkholeDamage: this.fb.control(
        GeneralUtils.parseStringFromBoolean(sinkholeDetails?.doesApplicantKnowPriorOwnerReportedSinkholeDamage)
        ?? 'false',
        [Validators.required]
      ),
    });
  }

  highlightRequiredErrors(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        if (GeneralUtils.isEmpty(control.value)) {
          this.errorMessage[key] = 'This field is required to quote.';
        } else {
          this.errorMessage[key] = '';
        }
        this.cd.detectChanges();
      }
    });
  }
}
